import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ClipboardList, Eye, Loader2, RefreshCw, Search } from 'lucide-react';
import { platformAdminAPI } from '../services/api';

function statusBadge(status) {
  const classes = {
    pending_review: 'bg-orange-50 text-orange-700 ring-orange-100',
    approved: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
    rejected: 'bg-red-50 text-red-700 ring-red-100',
    failed: 'bg-slate-100 text-slate-700 ring-slate-200',
  };

  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${classes[status] || classes.failed}`}>
      {String(status || '').replaceAll('_', ' ').toUpperCase()}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export default function PlatformAdminOnboardingRequestsPage() {
  const navigate = useNavigate();

  const [status, setStatus] = useState('pending_review');
  const [search, setSearch] = useState('');
  const [requests, setRequests] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadRequests = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await platformAdminAPI.listOnboardingRequests({
        status,
        search,
      });

      const data = res.data;
      setRequests(data.results || data || []);
      setCount(data.count || (data.results || data || []).length);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load onboarding requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const submitSearch = (e) => {
    e.preventDefault();
    loadRequests();
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="rounded-2xl bg-orange-100 p-3 text-orange-700">
              <ClipboardList className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-950">
                PII Onboarding Requests
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Review Consent Platform verified onboarding requests before PII infra is provisioned.
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={loadRequests}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {['pending_review', 'approved', 'rejected', 'failed', 'all'].map((item) => (
              <button
                key={item}
                onClick={() => setStatus(item)}
                className={`rounded-xl px-3 py-2 text-sm font-semibold ${
                  status === item
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {item.replaceAll('_', ' ')}
              </button>
            ))}
          </div>

          <form onSubmit={submitSearch} className="flex min-w-0 gap-2 lg:w-96">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search org, requester, code..."
                className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              />
            </div>
            <button
              type="submit"
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Search
            </button>
          </form>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-5">
          <p className="text-sm font-semibold text-slate-950">
            {count} request{count === 1 ? '' : 's'}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-10 text-slate-600">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-orange-600" />
            Loading requests...
          </div>
        ) : requests.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            No onboarding requests found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-6 py-3">Request</th>
                  <th className="px-6 py-3">Organization</th>
                  <th className="px-6 py-3">Requester</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Submitted</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="font-mono text-xs font-semibold text-slate-950">
                        {request.request_code}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {request.id}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-950">
                        {request.consent_org_name}
                      </div>
                      <div className="mt-1 font-mono text-xs text-slate-500">
                        {request.consent_org_uuid}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-slate-900">{request.requester_name}</div>
                      <div className="mt-1 text-xs text-slate-500">{request.requester_email}</div>
                    </td>

                    <td className="px-6 py-4">
                      {statusBadge(request.status)}
                    </td>

                    <td className="px-6 py-4 text-slate-600">
                      {formatDate(request.submitted_at)}
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => navigate(`/platform-admin/onboarding-requests/${request.id}`)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}